import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LopRecordForm,
  SaveCancelledError,
  type LopFieldDef,
} from '../../components/LopRecordForm';
import { Hash, FileText, Layers, Globe, Landmark } from 'lucide-react';
import { useTabTitle } from '../../tabs';
import { getFund, saveBatchFunds } from '../../api/funds';
import { checkFundFeeLog } from '../../api/fundFeeLog';
import { useFundTypes } from '../../hooks/useFundTypes';
import { useCrcies } from '../../hooks/useCrcies';
import {
  useFundClassifications,
  useIsaFundClassifications,
  useProspectusExposureTypes,
  useOperators,
  useTrustees,
  useCompanies,
} from '../../hooks/fundLookups';
import type { MutualFund, MutualFundUpdate } from '../../types/MutualFund';
import type { FeeType, FundFeeLogCheckRow } from '../../types/FundFeeLog';
import { t } from '../../i18n/t';
import { FundAccountsPanel } from './FundAccountsPanel';
import { FundIndicesPanel } from './FundIndicesPanel';
import {
  FeeChangeConfirmDialog,
  type FeeChangeChoice,
} from './FeeChangeConfirmDialog';

// Fee-percent fields checked on save (V1.0.53/54 TrusteeFeeLog/
// ManagementFeeLog). Maps each to its GET /api/fund-fee-log/check feeType
// and the MutualFundUpdate param the backend expects when the user picks
// "update the existing row" (see FundRecordPage.saveRecord below).
const FEE_FIELDS: {
  field: 'trusteeFeePercent' | 'managementFeePercent';
  feeType: FeeType;
  recIdParam: 'trusteeFeeLogRecIdToUpdate' | 'managementFeeLogRecIdToUpdate';
}[] = [
  { field: 'trusteeFeePercent', feeType: 'trustee', recIdParam: 'trusteeFeeLogRecIdToUpdate' },
  { field: 'managementFeePercent', feeType: 'management', recIdParam: 'managementFeeLogRecIdToUpdate' },
];

interface FeeDialogState {
  feeType: FeeType;
  row: FundFeeLogCheckRow;
  resolve: (choice: FeeChangeChoice) => void;
}

const newFund = (): MutualFund => ({
  fundId: 0,
  fundName: '',
  fundOfficialName: null,
  fundTypeId: 1,
  fundAssetId: 0,
  tradingStartDate: null,
  issuerId: null, issuedQuantity: null, conversionPercent: null, managementFeePercent: null, managementFeeStartDate: null,
  includedInIsaReport: null, isActive: null, name4Isa: null, trusteeId: null, leverage: null, fundClassificationId: null, crcyId: null,
  createdDate: null, allow2HoldInternal: null, allow2BeInternal: null, isForeign: null, nickName: null, nameEn: null, companyId: null,
  magnaNumber: null, lastTrusteePaymentDate: null, trusteeFeePercent: null, dynamicManagementFeePercent: null, isComposite: null,
  indexRateDmfFirstDay: null, fundRateDmfFirstDay: null, balanceMfFirstDay: null, balanceTrusteeFeeFirstDay: null, balanceDmfFirstDay: null,
  lastValuationAt: null, lastManagementFeePaymentDate: null, dmfStartDate: null, crcyRateDmfFirstDay: null, trusteeFeeStartDate: null,
  valuationOrderGroup: null, isPreMarketExchange: null, interestCrcyTicker: null, firstDayIndexCrcyRate: null, isLatePreMarketExchange: null,
  sample1730AtDmfStartDate: null, lastTradeDate: null, eodCrcyId: null, isaFundClassificationId: null, isCollateralInCreditLeverage: null,
  determiningDate: null, prospectusExposureTypeId: null, operatorId: null, creationSpread: null, redemptionSpread: null, nominalCrcyId: null,
  dmfPsagotPlugNumber: null, redemptionLimitsStartDate: null,
  firstTradeDateNav: null,
});

export function FundRecordPage() {
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const initialId = useMemo(() => {
    if (idParam == null) return null;
    const n = Number(idParam);
    return Number.isFinite(n) ? n : null;
  }, [idParam]);

  const fundTypes = useFundTypes();
  const crcies = useCrcies();
  const fundClassifications = useFundClassifications();
  const isaFundClassifications = useIsaFundClassifications();
  const prospectusExposureTypes = useProspectusExposureTypes();
  const operators = useOperators();
  const trustees = useTrustees();
  const companies = useCompanies();

  // Internal tabs (plan §3.6): the tab starts with the router-derived default
  // ("הגדרת קרן" / "הגדרת קרן · #9") — once a real record loads (or the user
  // goes back to "new"), override it with the fund's own name so switching
  // via Alt+Q/the tab bar shows something meaningful.
  const [tabTitle, setTabTitle] = useState<string | null>(null);
  useTabTitle(tabTitle);

  // Snapshot of the last record LOADED from the server (via loadRecord below,
  // or adopted after a successful save) — NOT the live edited state. Used to
  // detect whether trusteeFeePercent/managementFeePercent actually CHANGED on
  // save (comparing against the edited record itself would always show "no
  // change" once the user has typed). Reset every time a fresh/persisted
  // record is adopted; irrelevant for isNew saves (a brand-new fund has no
  // "previous value" to diff, so the fee-log check is skipped entirely then).
  const baselineRef = useRef<MutualFund | null>(null);

  // Fee-change confirmation dialog — a Promise-based bridge from the
  // (synchronous-looking) saveRecord flow to the async user choice. Only one
  // can be open at a time (trustee then management, sequentially).
  const [feeDialog, setFeeDialog] = useState<FeeDialogState | null>(null);
  const askFeeChange = useCallback(
    (feeType: FeeType, row: FundFeeLogCheckRow) =>
      new Promise<FeeChangeChoice>((resolve) => {
        setFeeDialog({ feeType, row, resolve });
      }),
    [],
  );
  const handleFeeChoice = useCallback((choice: FeeChangeChoice) => {
    setFeeDialog((prev) => {
      prev?.resolve(choice);
      return null;
    });
  }, []);

  // Gate only on the CENTRAL lookups. companies is tenant-scoped and can race
  // tenant-init on a hard reload — best-effort (its dropdown fills when loaded).
  const gateLookups = [fundTypes, crcies, fundClassifications, isaFundClassifications, prospectusExposureTypes, operators, trustees];
  const anyError = gateLookups.map((l) => l.error).find(Boolean) ?? null;
  const loading = gateLookups.some((l) => l.loading) || gateLookups.some((l) => !l.data);

  const opts = useMemo(() => {
    const crcyOpts = (crcies.data ?? []).map((c) => ({ id: c.crcyId, name: c.crcyName }));
    return {
      fundType: (fundTypes.data ?? []).map((x) => ({ id: x.fundTypeId, name: x.fundTypeName })),
      crcy: crcyOpts,
      fundClassification: (fundClassifications.data ?? []).map((x) => ({ id: x.fundClassificationId, name: x.fundClassificationName })),
      isaFundClassification: (isaFundClassifications.data ?? []).map((x) => ({ id: x.isaFundClassificationId, name: x.isaFundClassificationName })),
      prospectusExposureType: (prospectusExposureTypes.data ?? []).map((x) => ({ id: x.prospectusExposureTypeId, name: x.prospectusExposureTypeName })),
      operator: (operators.data ?? []).map((x) => ({ id: x.operatorId, name: x.operatorName })),
      trustee: (trustees.data ?? []).map((x) => ({ id: x.trusteeId, name: x.trusteeName })),
      company: (companies.data ?? []).map((x) => ({ id: x.companyId, name: x.companyHebName })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, companies.data]);

  const fieldDefs = useMemo<LopFieldDef[]>(() => {
    const SID = t('funds.sections.identity');
    const SCLS = t('funds.sections.classification');
    const SCUR = t('funds.sections.currency');
    const SFEE = t('funds.sections.fees');
    const SFLAG = t('funds.sections.flags');
    const SDV = t('funds.sections.datesValuation');
    const col = (f: string) => t(`funds.cols.${f}` as Parameters<typeof t>[0]);

    return [
      // ── identity ──
      { field: 'fundName', label: t('funds.columnFundName'), type: 'text', required: true, section: SID },
      { field: 'fundOfficialName', label: t('funds.columnFundOfficialName'), type: 'text', section: SID },
      { field: 'fundAssetId', label: t('funds.columnFundAssetId'), type: 'number', required: true, section: SID },
      { field: 'name4Isa', label: col('name4Isa'), type: 'text', section: SID },
      { field: 'nickName', label: col('nickName'), type: 'text', section: SID },
      { field: 'nameEn', label: col('nameEn'), type: 'text', section: SID },
      { field: 'magnaNumber', label: col('magnaNumber'), type: 'number', section: SID },
      // ── classification & links ──
      { field: 'fundTypeId', label: t('funds.columnFundType'), type: 'fk', required: true, fkOptions: opts.fundType, section: SCLS },
      { field: 'issuerId', label: col('issuerId'), type: 'number', section: SCLS },
      { field: 'trusteeId', label: col('trusteeId'), type: 'fk', fkAllowNone: true, fkOptions: opts.trustee, section: SCLS },
      { field: 'fundClassificationId', label: col('fundClassificationId'), type: 'fk', fkAllowNone: true, fkOptions: opts.fundClassification, section: SCLS },
      { field: 'isaFundClassificationId', label: col('isaFundClassificationId'), type: 'fk', fkAllowNone: true, fkOptions: opts.isaFundClassification, section: SCLS },
      { field: 'companyId', label: col('companyId'), type: 'fk', fkAllowNone: true, fkOptions: opts.company, section: SCLS },
      { field: 'operatorId', label: col('operatorId'), type: 'fk', fkAllowNone: true, fkOptions: opts.operator, section: SCLS },
      { field: 'prospectusExposureTypeId', label: col('prospectusExposureTypeId'), type: 'fk', fkAllowNone: true, fkOptions: opts.prospectusExposureType, section: SCLS },
      { field: 'leverage', label: col('leverage'), type: 'number', section: SCLS },
      { field: 'issuedQuantity', label: col('issuedQuantity'), type: 'number', section: SCLS },
      // ── currency ──
      { field: 'crcyId', label: col('crcyId'), type: 'fk', fkAllowNone: true, fkOptions: opts.crcy, section: SCUR },
      { field: 'nominalCrcyId', label: col('nominalCrcyId'), type: 'fk', fkAllowNone: true, fkOptions: opts.crcy, section: SCUR },
      { field: 'eodCrcyId', label: col('eodCrcyId'), type: 'fk', fkAllowNone: true, fkOptions: opts.crcy, section: SCUR },
      { field: 'interestCrcyTicker', label: col('interestCrcyTicker'), type: 'text', section: SCUR },
      { field: 'firstDayIndexCrcyRate', label: col('firstDayIndexCrcyRate'), type: 'number', section: SCUR },
      { field: 'crcyRateDmfFirstDay', label: col('crcyRateDmfFirstDay'), type: 'number', section: SCUR },
      // ── fees ──
      { field: 'managementFeePercent', label: col('managementFeePercent'), type: 'number', section: SFEE },
      { field: 'managementFeeStartDate', label: col('managementFeeStartDate'), type: 'date', section: SFEE },
      { field: 'trusteeFeePercent', label: col('trusteeFeePercent'), type: 'number', section: SFEE },
      { field: 'trusteeFeeStartDate', label: col('trusteeFeeStartDate'), type: 'date', section: SFEE },
      { field: 'dynamicManagementFeePercent', label: col('dynamicManagementFeePercent'), type: 'number', section: SFEE },
      { field: 'dmfStartDate', label: col('dmfStartDate'), type: 'date', section: SFEE },
      { field: 'lastManagementFeePaymentDate', label: col('lastManagementFeePaymentDate'), type: 'date', section: SFEE },
      { field: 'lastTrusteePaymentDate', label: col('lastTrusteePaymentDate'), type: 'date', section: SFEE },
      { field: 'conversionPercent', label: col('conversionPercent'), type: 'number', section: SFEE },
      { field: 'creationSpread', label: col('creationSpread'), type: 'number', section: SFEE },
      { field: 'redemptionSpread', label: col('redemptionSpread'), type: 'number', section: SFEE },
      // ── flags ──
      { field: 'isActive', label: col('isActive'), type: 'bit', section: SFLAG },
      { field: 'isForeign', label: col('isForeign'), type: 'bit', section: SFLAG },
      { field: 'isComposite', label: col('isComposite'), type: 'bit', section: SFLAG },
      { field: 'includedInIsaReport', label: col('includedInIsaReport'), type: 'bit', section: SFLAG },
      { field: 'isPreMarketExchange', label: col('isPreMarketExchange'), type: 'bit', section: SFLAG },
      { field: 'isLatePreMarketExchange', label: col('isLatePreMarketExchange'), type: 'bit', section: SFLAG },
      { field: 'isCollateralInCreditLeverage', label: col('isCollateralInCreditLeverage'), type: 'bit', section: SFLAG },
      { field: 'allow2HoldInternal', label: col('allow2HoldInternal'), type: 'bit', section: SFLAG },
      { field: 'allow2BeInternal', label: col('allow2BeInternal'), type: 'bit', section: SFLAG },
      // ── dates & valuation ──
      { field: 'tradingStartDate', label: t('funds.columnTradingStartDate'), type: 'date', section: SDV },
      { field: 'firstTradeDateNav', label: col('firstTradeDateNav'), type: 'number', section: SDV, tooltip: t('funds.tooltips.firstTradeDateNav') },
      { field: 'createdDate', label: col('createdDate'), type: 'date', section: SDV },
      { field: 'lastValuationAt', label: col('lastValuationAt'), type: 'date', section: SDV },
      { field: 'valuationOrderGroup', label: col('valuationOrderGroup'), type: 'text', section: SDV },
      { field: 'indexRateDmfFirstDay', label: col('indexRateDmfFirstDay'), type: 'number', section: SDV },
      { field: 'fundRateDmfFirstDay', label: col('fundRateDmfFirstDay'), type: 'number', section: SDV },
      { field: 'balanceMfFirstDay', label: col('balanceMfFirstDay'), type: 'number', section: SDV },
      { field: 'balanceTrusteeFeeFirstDay', label: col('balanceTrusteeFeeFirstDay'), type: 'number', section: SDV },
      { field: 'balanceDmfFirstDay', label: col('balanceDmfFirstDay'), type: 'number', section: SDV },
      { field: 'sample1730AtDmfStartDate', label: col('sample1730AtDmfStartDate'), type: 'number', section: SDV },
      { field: 'dmfPsagotPlugNumber', label: col('dmfPsagotPlugNumber'), type: 'number', section: SDV },
      { field: 'lastTradeDate', label: col('lastTradeDate'), type: 'date', section: SDV },
      { field: 'determiningDate', label: col('determiningDate'), type: 'date', section: SDV },
      { field: 'redemptionLimitsStartDate', label: col('redemptionLimitsStartDate'), type: 'date', section: SDV },
    ];
  }, [opts]);

  if (anyError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-error-text)', background: 'var(--color-error-bg)', padding: '8px 12px', borderRadius: 4 }}>
          {t('funds.errorPrefix', { message: anyError })}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <>
    <LopRecordForm<MutualFund>
      title={t('funds.formTitle')}
      fieldDefs={fieldDefs}
      idField="fundId"
      searchTable="MutualFunds"
      searchColumns={[
        { field: 'fundId', headerName: t('funds.columnFundId') },
        { field: 'fundName', headerName: t('funds.columnFundName') },
        { field: 'fundOfficialName', headerName: t('funds.columnFundOfficialName') },
        { field: 'fundAssetId', headerName: t('funds.columnFundAssetId') },
      ]}
      suggestionLabel={(r) => `${r.fundId} - ${r.fundName ?? ''}`}
      loadRecord={async (id) => {
        const rec = await getFund(id);
        baselineRef.current = rec;
        return rec;
      }}
      saveRecord={async (rec, isNew) => {
        // Fee-change guard (Nitzan, 15/09/2026 — TrusteeFeeLog/
        // ManagementFeeLog contract): only for an existing fund, and only
        // for fields that actually changed from the LOADED value (not just
        // "touched"). Sequential per field — trustee first, then management
        // — each shows its own dialog if the log has a nearby row.
        const extra: Pick<MutualFundUpdate, 'trusteeFeeLogRecIdToUpdate' | 'managementFeeLogRecIdToUpdate'> = {};
        const base = baselineRef.current;
        if (!isNew && base) {
          for (const { field, feeType, recIdParam } of FEE_FIELDS) {
            if (rec[field] === base[field]) continue;
            const result = await checkFundFeeLog(rec.fundId, feeType);
            if (result.rows.length === 0) continue;
            const choice = await askFeeChange(feeType, result.rows[0]);
            if (choice === 'cancel') {
              throw new SaveCancelledError(t('fundFeeChange.cancelledStatus'));
            }
            if (choice === 'update') {
              extra[recIdParam] = result.rows[0].recId;
            }
          }
        }

        const list = isNew
          ? await saveBatchFunds([rec], [], [])
          : await saveBatchFunds([], [{ ...rec, ...extra }], []);
        const persisted = isNew
          ? (list.find((f) => f.fundAssetId === rec.fundAssetId) ?? rec)
          : (list.find((f) => f.fundId === rec.fundId) ?? rec);
        baselineRef.current = persisted;
        return persisted;
      }}
      newRecord={newFund}
      initialId={initialId}
      searchPlaceholder={t('funds.searchPlaceholder')}
      onRecordChange={(record, mode) => {
        setTabTitle(
          mode === 'edit' && record.fundId > 0
            ? `${t('funds.formTitle')} · ${record.fundName || record.fundId}`
            : t('funds.formTitle'),
        );
      }}
      headerIdentity={(rec, isNew) => {
        if (isNew) return { name: null, chips: [] };
        const typeName = (fundTypes.data ?? []).find((x) => x.fundTypeId === rec.fundTypeId)?.fundTypeName;
        return {
          name: rec.fundName || null,
          chips: [
            { key: 'fundId', label: t('funds.columnFundId'), value: rec.fundId > 0 ? String(rec.fundId) : '', icon: Hash, inline: true },
            { key: 'fundAssetId', label: t('funds.chipAssetNo'), value: rec.fundAssetId ? String(rec.fundAssetId) : '', icon: FileText, inline: true },
            { key: 'fundType', label: t('funds.columnFundType'), value: typeName ?? '', icon: Layers },
            rec.isForeign
              ? { key: 'origin', label: '', value: t('funds.chipForeign'), icon: Globe }
              : { key: 'origin', label: '', value: t('funds.chipIsraeli'), icon: Landmark },
          ],
        };
      }}
      summaryFields={[
        { field: 'fundId', label: t('funds.columnFundId') },
        { field: 'fundName', label: t('funds.columnFundName') },
        { field: 'fundAssetId', label: t('funds.columnFundAssetId') },
      ]}
      renderExtraPanel={({ record, mode }) =>
        mode === 'edit' && record.fundId > 0 ? (
          <>
            <FundIndicesPanel fundId={record.fundId} />
            <FundAccountsPanel fundId={record.fundId} fundName={record.fundName} />
          </>
        ) : null
      }
    />
    {feeDialog && (
      <FeeChangeConfirmDialog
        feeType={feeDialog.feeType}
        row={feeDialog.row}
        onChoice={handleFeeChoice}
      />
    )}
    </>
  );
}
